import { Injectable } from "@angular/core";

import { Subscription, lastValueFrom } from "rxjs";
import { first, map, take } from "rxjs/operators";

import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Timestamp } from "@firebase/firestore-types";

import { CONFIG } from "@constants/config";
import { ICurrentUser, IEmailTemplate } from "@interfaces/user.interface";
import { IRequest } from "@interfaces/request.interface";

import { RemoteSupportService } from "@services/remote-support.service";
import { RemoteUserService } from "@state/remote-user/remote-user.service";

import { UserStore } from "./user.store";
import { UserQuery } from "./user.query";
import { DeviceService } from "../device/device.service";
import { ISignUp } from "@shared/interfaces/sign-up.interface";

import {
  getFirestore,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp as firestoreServerTimestamp,
} from "@firebase/firestore";
import { Storage, ref as storageRef, getMetadata } from "@angular/fire/storage";
import {
  remove,
  getDatabase,
  ref,
  update,
  onValue,
  onDisconnect,
  serverTimestamp,
  update as updateRTDB,
} from "firebase/database";
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

//import * as firebase from 'firebase';

import { INewPassword } from "@shared/interfaces/new-password.interface";
import { ToastService } from "@shared/services/toast.service";
import { FirestoreService } from "@shared/services/firestore.service";
import { AlarmService } from "../alarm/alarm.service";
import { TranslateService } from "@ngx-translate/core";
import { AlertController, Platform } from "@ionic/angular";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "@env/environment";

@Injectable({
  providedIn: "root",
})
export class UserService {
  private handledRequests: string[] = [];

  private userSubscription: Subscription;
  private remoteSubscription: Subscription;
  private roleSubscription: Subscription;

  private db = getDatabase();
  private firestore = getFirestore();

  constructor(
    private angularFirestore: AngularFirestore,
    private userStore: UserStore,
    private userQuery: UserQuery,
    private remoteSupportService: RemoteSupportService,
    private remoteUserService: RemoteUserService,
    private deviceService: DeviceService,
    private toastService: ToastService,
    private firestoreService: FirestoreService,
    private translateService: TranslateService,
    private alertController: AlertController,
    private platform: Platform,
    private alarmService: AlarmService,
    private httpClient: HttpClient,
    private angularFireStorage: Storage,
  ) {}

  public setUserState(userUid: string): void {
    this.userSubscription = this.angularFirestore
      .collection(CONFIG.firebaseCollection.users)
      .doc<ICurrentUser>(userUid)
      .valueChanges()
      .subscribe((user) => {
        if (user) {
          this.userStore.update((state) => {
            return {
              ...state,
              uid: userUid,
              currentUser: user,
            };
          });
          this.userStore.setLoading(false);
        }
      });

    this.roleSubscription = this.userQuery.role$
      ?.pipe(take(2))
      .subscribe((role) => {
        // console.log('role: ', role);
        if (role === "operator" || role === "developer") {
          this.remoteSubscription = this.angularFirestore
            .collection(CONFIG.firebaseCollection.users)
            .doc<ICurrentUser>(userUid)
            .collection<IRequest>(CONFIG.firebaseCollection.remoteRequests)
            .valueChanges({ idField: "uid" })
            .pipe(
              map((request) => request.filter((data) => !data.responded)),
              map((request) =>
                request.map((data) => ({
                  ...data,
                  requestDate:
                    data.requestDate &&
                    (data.requestDate as Timestamp).toDate(),
                  responseDate:
                    data.responseDate &&
                    (data.responseDate as Timestamp).toDate(),
                })),
              ),
            )
            .subscribe((request) => {
              // console.log('request: ', request);
              // Start some form of modal, grab data from constats or something
              // Require user to either accept/decline

              if (request.length > 0) {
                let date: Date;
                request.forEach((element) => {
                  if (!date) {
                    date = element.requestDate;
                  } else if (element.requestDate > date) {
                    date = element.requestDate;
                  }
                });
                const theRequest = request.find((x) => x.requestDate === date);

                //console.log('auto', theRequest);
                // const requestRecievedTime = new Date();
                // requestRecievedTime.setMinutes(requestRecievedTime.getMinutes() - 1);
                // var liveRequest = requestRecievedTime < theRequest.requestDate;

                if (
                  theRequest &&
                  !this.handledRequests.includes(theRequest.uid)
                ) {
                  this.handledRequests.push(theRequest.uid);

                  var isOnTime = false;
                  var dateNow = new Date();
                  dateNow.setSeconds(dateNow.getSeconds() - 10);
                  if (dateNow < theRequest.requestDate) {
                    isOnTime = true;
                  }
                  // console.log('auto', theRequest.type);
                  switch (theRequest.type) {
                    case "remote-support":
                      //  console.log('REMOTE SUPPORT');
                      if (isOnTime) {
                        this.remoteSupportService.showOperatorRemoteSupportAlert(
                          userUid,
                          theRequest,
                        );
                      }
                      break;
                    case "remote-support-stop":
                      //console.log('REMOTE SUPPORT STOP');
                      this.firestoreService.endLiveData();
                      if (isOnTime) {
                        this.remoteSupportService.showOperatorRemoteSupportStopAlert(
                          userUid,
                          theRequest,
                        );
                      }

                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      this.remoteUserService.setRemoteSessionInActive;
                      break;
                    case "download-firmware":
                      // console.log('theRequest', theRequest);
                      this.handleFirmwareDownload(userUid, theRequest);

                      break;
                    case "update-firmware":
                      //console.log("theRequest", theRequest);
                      this.handleFirmwareUpdateForSparePart(
                        userUid,
                        theRequest,
                      );

                      break;
                    case "update-parameters":
                      // console.log('update-parameters', isOnTime);
                      if (isOnTime) {
                        this.deviceService.addParameterForUpdate(theRequest);
                      }
                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      // this.remoteSupportService.showOperatorParameterUpdateAlert(theRequest);
                      break;
                    case "update-string-parameters":
                      //console.log('update-string-parameters');
                      if (isOnTime) {
                        this.deviceService.addStringParameterForUpdate(
                          theRequest,
                        );
                      }
                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      // this.remoteSupportService.showOperatorParameterUpdateAlert(theRequest);
                      break;
                    case "auto-calibrate":
                      if (theRequest.stopCalibrate) {
                        this.deviceService.stopAutoCalibration(
                          theRequest.stopCalibrate,
                        );
                        this.remoteSupportService.alertOperatorOnAutoCalibrate(
                          theRequest,
                          theRequest.stopCalibrate,
                        );
                      } else {
                        //  console.log('auto-calibrate');
                        const dateNow = new Date();
                        dateNow.setSeconds(dateNow.getSeconds() - 10);
                        if (dateNow < theRequest.requestDate) {
                          this.deviceService.addParameterForAutoCalibration(
                            theRequest.autoCalibrateParameters,
                          );
                          this.remoteSupportService.alertOperatorOnAutoCalibrate(
                            theRequest,
                            theRequest.stopCalibrate,
                          );
                        }
                      }
                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      break;
                    case "live-data":
                      // console.log('live-data');
                      if (isOnTime) {
                        this.remoteSupportService.showOperatorLiveDataAlert(
                          theRequest,
                        );
                        this.firestoreService.startLiveData();
                      }
                      // console.log(userUid, theRequest.uid);
                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );

                      break;
                    case "clear-alarm":
                      //console.log('clear-alarm');
                      if (isOnTime && theRequest.alarmToClear) {
                        this.alarmService.addAlarmToBeCleared(
                          theRequest.alarmToClear,
                          theRequest.uid,
                        );
                      }

                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      break;
                    case "clear-alarm-log":
                      //console.log('clear-alarm-log');
                      // this.alarmService.clearLog()
                      if (isOnTime && theRequest.clearType) {
                        this.alarmService.addClearAlarmLog(
                          theRequest.clearType,
                          theRequest.uid,
                        );
                      }

                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      break;
                    case "get-alarm-log":
                      //console.log('get-alarm-log');

                      if (isOnTime) {
                        this.alarmService.addGetAlarmLog();
                      }
                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                      break;
                    case "get-parameters":
                      //console.log('get-parameters');
                      if (isOnTime) {
                        this.deviceService.getStringParameters(
                          true,
                          theRequest.parametersToFetch,
                        );
                      }

                      this.remoteSupportService.updateFirestoreRequest(
                        userUid,
                        theRequest.uid,
                        true,
                      );
                    default:
                      break;
                  }
                }
              }
            });
        } else if (
          role === "service" ||
          role === "superadmin" ||
          role === "admin" ||
          role === "localadmin"
        ) {
          this.remoteSubscription = this.angularFirestore
            .collection(CONFIG.firebaseCollection.users)
            .doc<ICurrentUser>(userUid)
            .collection<IRequest>(CONFIG.firebaseCollection.remoteRequests)
            .valueChanges({ idField: "uid" })
            .pipe(
              map((request) => request.filter((data) => !data.responded)),
              map((request) =>
                request.map((data) => ({
                  ...data,
                  requestDate:
                    data.requestDate &&
                    (data.requestDate as Timestamp).toDate(),
                  responseDate:
                    data.responseDate &&
                    (data.responseDate as Timestamp).toDate(),
                })),
              ),
            )
            .subscribe((request) => {
              ////console.log('request: ', request);
              // Start some form of modal, grab data from constats or something
              // Require user to either accept/decline
              if (request.length > 0) {
                let date: Date | undefined;

                request.forEach((element) => {
                  if (!date) {
                    date = element.requestDate;
                  } else if (element.requestDate > date) {
                    date = element.requestDate;
                  }
                });
                const theRequest = request.find((x) => x.requestDate === date);
                var isOnTime = false;
                var dateNow = new Date();
                dateNow.setSeconds(dateNow.getSeconds() - 10);
                if (!theRequest) {
                  return;
                }
                if (dateNow < theRequest.requestDate) {
                  isOnTime = true;
                }
                // const requestRecievedTime = new Date();
                // requestRecievedTime.setMinutes(requestRecievedTime.getMinutes() - 1);
                // var liveRequest = requestRecievedTime < theRequest.requestDate;
                switch (theRequest.type) {
                  case "remote-support-stop":
                    //console.log('REMOTE SUPPORT STOP');
                    if (isOnTime) {
                      this.remoteSupportService.showServiceRemoteSupportStopAlert(
                        userUid,
                        theRequest,
                      );
                    }

                    this.remoteSupportService.updateFirestoreRequest(
                      userUid,
                      theRequest.uid,
                      true,
                    );
                    break;
                  default:
                    break;
                }
              }
            });
        }
      });
  }

  public serviceToolBleIsActive(bool: boolean) {
    this.userStore.update(() => ({ serviceToolHasBleConnection: bool }));
  }

  public setStopAutoCalibration(bool: boolean) {
    this.userStore.update((state) => {
      return {
        ...state,
        autoCalibrationStarted: bool,
      };
    });
  }

  public updateLatestConnectionInfo(): void {
    //this.subscribeCurrentDeviceSubscription?.unsubscribe();
    this.firestoreService.removeConnectedDevices();
    this.firestoreService.updateLatestConnection(this.userQuery.userUid);
  }

  public notConnectedToDevice(bool: boolean) {
    this.userStore.update(() => ({ notConnectedToDevice: bool }));
  }

  public unsubscribe() {
    this.userSubscription?.unsubscribe();
    this.roleSubscription?.unsubscribe();

    if (this.remoteSubscription) {
      this.remoteSubscription.unsubscribe;
    }
  }

  public changeRole(userId: string, newRole: string) {
    this.angularFirestore
      .collection(CONFIG.firebaseCollection.users)
      .doc(userId)
      .update({ role: newRole });
  }

  public updateUser(data: ISignUp): Promise<void> {
    const { firstName, lastName, email, phoneNumber } = data;
    const userProfile = {
      firstName,
      lastName,
      email,
      phoneNumber,
    };
    const userUid = this.userQuery.userUid;
    return this.angularFirestore
      .collection(CONFIG.firebaseCollection.users)
      .doc(userUid)
      .update(userProfile)
      .then((res) => {
        this.toastService.successToast(
          "toasts.profile update succeeded",
          true,
          "toasts.profile update",
        );
      })
      .catch((err) => {
        this.toastService.warningToast("toasts.profile update failed", true);
        throw "something went wrong";
      });
  }

  async changePassword(data: INewPassword): Promise<void> {
    const { password, newPassword, confirmNewPassword } = data;

    if (newPassword !== confirmNewPassword) {
      this.toastService.dangerToast("Passwords do not match", true);
      throw new Error("Passwords do not match");
    }
    const user = getAuth().currentUser;
    if (!user || !user.email) {
      this.toastService.dangerToast("auth.something went wrong", true);
      throw new Error("User is not authenticated");
    }
    const credentials = EmailAuthProvider.credential(user.email, password);

    try {
      // Reauthenticate with the current user's credentials
      await reauthenticateWithCredential(user, credentials);

      // Update the user's password
      await updatePassword(user, newPassword);

      this.toastService.successToast(
        "auth.password update succeeed",
        true,
        "toasts.profile update",
      );
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };

      if (firebaseError.code === "auth/wrong-password") {
        this.toastService.warningToast("auth.wrong password", true);
        throw new Error("wrong password");
      }

      this.toastService.dangerToast("auth.something went wrong", true);
      throw new Error("something went wrong");
    }
  }

  public goOffline() {
    var uid = getAuth().currentUser?.uid;
    if (uid) {
      var userStatusFirestoreRef = doc(this.firestore, "/users/" + uid);
      var userStatusDatabaseRef = ref(this.db, "/status/" + uid);
      var isOfflineForFirestore = {
        state: "offline",
        last_changed: firestoreServerTimestamp(),
      };
      var isOfflineForDatabase = {
        state: "offline",
        last_changed: { ".sv": "timestamp" },
      };

      try {
        // Update Firestore document
        updateDoc(userStatusFirestoreRef, isOfflineForFirestore);

        // Update Realtime Database reference
        update(userStatusDatabaseRef, isOfflineForDatabase);
      } catch (error) {
        console.error("Error updating user status:", error);
      }
      if (this.userSubscription) {
        this.userSubscription.unsubscribe();
      }
      if (this.remoteSubscription) {
        this.remoteSubscription.unsubscribe;
      }
      if (this.roleSubscription) {
        this.roleSubscription.unsubscribe;
      }
    }
  }

  public rtdb_and_local_fs_presence() {
    var uid = getAuth().currentUser?.uid;
    var userStatusDatabaseRef = ref(this.db, "/status/" + uid);
    var userStatusFirestoreRef = doc(this.firestore, "/users/" + uid);
    var userDevicesRef = ref(this.db, `/status/${uid}/connectedDevices`);

    const connectedRef = ref(this.db, ".info/connected");

    onValue(connectedRef, (snapshot) => {
      if (snapshot.val() === false) {
        // Set Firestore's state to 'offline'
        updateDoc(userStatusFirestoreRef, {
          state: "offline",
          last_changed: firestoreServerTimestamp(),
          connectedDevices: "",
        });
        return;
      }

      onDisconnect(userStatusDatabaseRef)
        .update({
          state: "offline",
          last_changed: serverTimestamp(),
        })
        .then(() => {
          updateRTDB(userStatusDatabaseRef, {
            state: "online",
            last_changed: serverTimestamp(),
          });

          // Also update Firestore when we come online
          updateDoc(userStatusFirestoreRef, {
            state: "online",
            last_changed: firestoreServerTimestamp(),
          }).catch(() => {});
        })
        .catch(() => {});

      onDisconnect(userDevicesRef)
        .remove()
        .catch(() => {});
    });
  }

  public reRenderDataTable(bool: boolean) {
    this.userStore.update(() => ({ dataTableBugFix: bool }));
  }

  public async deleteUserAlert() {
    const message = this.translateService.instant(
      "alerts.delete account message",
    );
    const doneMessage = this.translateService.instant(
      "alerts.delete account message done",
    );
    const failedMessage = this.translateService.instant(
      "alerts.delete account message failed",
    );
    const header = this.translateService.instant(
      "alerts.delete account header",
    );
    const cancelButton = this.translateService.instant("alerts.cancel");
    const deleteButton = this.translateService.instant("alerts.delete");

    const body = `
      <div class="text-center">
        <div class="m-auto flex justify-center">
          <ion-icon class="delete-alert-icon" name="trash"></ion-icon>
        </div>
      
        <p class="text-26pt text-roto-green py-2 font-semibold">${header}</p>
        <p class="text-18pt text-roto-green">${message}</p>
      </div>`;

    const waiting = `
      <div class="text-center">
        <div class="m-auto flex justify-center">
          <ion-icon class="delete-alert-icon" name="trash"></ion-icon>
        </div>

      <p class="text-26pt text-roto-green py-2 font-semibold">${header}</p>
      <div class="py-4">
        <ion-spinner></ion-spinner>
      </div>
        
      </div>`;

    const done = `
      <div class="text-center">
        <div class="m-auto flex justify-center">
          <ion-icon class="delete-alert-icon" name="trash"></ion-icon>
        </div>
    
        <p class="text-26pt text-roto-green py-2 font-semibold">${header}</p>
        <p class="text-18pt text-roto-green">${doneMessage}</p>
        
      </div>`;

    const failed = `
      <div class="text-center">
        <div class="m-auto flex justify-center">
          <ion-icon class="delete-alert-icon" name="trash"></ion-icon>
        </div>
    
        <p class="text-26pt text-roto-green py-2 font-semibold">${header}</p>
        <p class="text-18pt text-roto-green">${failedMessage}</p>
        
      </div>`;

    const alert = await this.alertController.create({
      message: body,
      keyboardClose: false,
      backdropDismiss: false,
      cssClass: "delete-user",
      buttons: [
        {
          text: cancelButton,
          role: "cancel",
        },
        {
          text: deleteButton,
          // role: 'delete',
          cssClass: "text-roto-blue",
          handler: () => {
            alert.message = waiting;
            this.sendDeleteEmail()
              .then((res: { status: number; message: string }) => {
                //console.log(res);
                if (res?.status !== 200) {
                  alert.message = failed;
                } else {
                  alert.message = done;
                  alert.buttons = [
                    {
                      text: "OK",
                      role: "ok",
                      cssClass: "no-button-borders",
                      handler: () => {
                        return true;
                      },
                    },
                  ];
                }
              })
              .catch((err) => {
                console.error(err);
                alert.message = failed;
              });

            return false;
          },
        },
      ],
    });

    await alert.present();
  }

  /*************handling firmware download alert in app**************** */

  public async handleFirmwareDownload(userUid: string, request: IRequest) {
    const state = await this.firmwareExists(request.firmwareUpgrade?.fullPath);

    if (state === "exists") {
      // Set activeFirmwarePath on user document so Storage rules allow download
    await this.setActiveFirmwarePath(userUid, request.firmwareUpgrade?.fullPath);
      this.remoteSupportService.showOperatorFirmwareDownloadAlert(
        userUid,
        request,
      );
    } else if (state === "missing") {
      await this.invalidateRequest(userUid, request.uid);
    } else if (state === "error") {
      console.log("something wrong");
    }
  }
  /*************handling firmware download alert in app**************** */

  public async handleFirmwareUpdateForSparePart(
    userUid: string,
    request: IRequest,
  ) {
   /* console.log(
      "handleFirmwareUpdateForSparePart",
      request?.firmwareUpgradeSparePat?.fullPath,
    );*/
    const state = await this.firmwareExists(
      request?.firmwareUpgradeSparePat?.fullPath,
    );

    if (state === "exists") {
        // Set activeFirmwarePath on user document so Storage rules allow download
    await this.setActiveFirmwarePath(userUid, request.firmwareUpgrade?.fullPath);
      this.remoteSupportService.showOperatorFirmwareUpdateAlert(
        userUid,
        request,
      );
    } else if (state === "missing") {
      await this.invalidateRequest(userUid, request?.uid);
    } else if (state === "error") {
      console.log("something wrong");
      await this.invalidateRequest(userUid, request?.uid);
    }
  }
  /*******check firmware exist or not******* */

  public async firmwareExists(
    path: string | undefined,
  ): Promise<"exists" | "missing" | "forbidden" | "error"> {
    try {
      await getMetadata(storageRef(this.angularFireStorage, path)); // storage reference for released version
      return "exists";
    } catch (e: any) {
      const code = e?.code || "";
      if (code === "storage/object-not-found") return "missing"; // no such file
      if (code === "storage/unauthorized") return "forbidden"; // blocked by rules
      return "error"; // network/other
    }
  }
/**************************************************************** */

private async setActiveFirmwarePath(userUid: string, filePath: string | undefined): Promise<void> {
  try {
    const userRef = doc(this.firestore, `users/${userUid}`);
    await setDoc(userRef, { activeFirmwarePath: filePath }, { merge: true });
  } catch (error) {
    console.error('Failed to set activeFirmwarePath:', error);
  }
}
  /************************delete request when firmware missing********************************* */
  private async invalidateRequest(
    userUid: string,
    requestId: string | undefined,
  ) {
    await remove(ref(this.db, `users/${userUid}/remoteRequests/${requestId}`));
  }
/*********************************************************************** */
public async clearActiveFirmwarePath(userUid: string): Promise<void> {
  try {
    const userRef = doc(this.firestore, `users/${userUid}`);
    await setDoc(userRef, { activeFirmwarePath: null }, { merge: true });
  } catch (error) {
    console.error('Failed to clear activeFirmwarePath:', error);
  }
}
  /************************************************************ */

  private async sendDeleteEmail() {
    var curUser = this.userQuery.currentUser;
    const { phoneNumber, email, customerNumber, company, lastName, firstName } =
      curUser;
    const subject = this.translateService.instant("email.subject");
    const text = this.translateService.instant("email.body", { user: curUser });
    var data: IEmailTemplate = {
      firstName,
      lastName,
      company: curUser.company ?? "",
      customerNumber: curUser.customerNumber ?? "",
      email,
      phoneNumber,
      subject,
      text,
      uid: this.userQuery.userUid,
    };

    const postRequest$ = this.httpClient.post<{
      status: number;
      message: string;
    }>(environment.functions.sendEmail, data, {
      headers: new HttpHeaders().set("Content-Type", "application/json"),
    });

    return await lastValueFrom(postRequest$);
  }
}
